import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { CheckCircle, ChevronRight, Package, Search, MapPin, ArrowDownLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const ReceivedList = ({ db, user }) => {
  const navigate = useNavigate();
  const [receivedItems, setReceivedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "inwards"), 
      where("status", "==", "qc_completed"),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const itemList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setReceivedItems(itemList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Received list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredItems = useMemo(() => {
    return receivedItems.filter(item => 
      item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.micNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [receivedItems, searchTerm]);

  const formatDate = (dateString) => {
      if (!dateString) return '---';
      try {
        const date = dateString.toDate ? dateString.toDate() : new Date(dateString);
        return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return '---';
      }
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ArrowDownLeft size={24} color="#16a34a" /> Received Materials
          </h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Log of all completed inbound QC records.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Supplier, Customer, MIC, or Ref No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
      </div>

      {receivedItems.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Package size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No received items yet.</h3>
          <p style={{ color: '#64748b' }}>Completed inbound QC records will appear here.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Receipt Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Supplier / Customer</th>
                <th style={thStyle}>Ref / Order No.</th>
                <th style={thStyle}>MIC No.</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr 
                  key={item.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => navigate(`/ops/received/${item.id}`)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold'}}>{formatDate(item.updatedAt)}</div>
                  </td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{item.supplierName || item.customerName}</div>
                    <div style={{fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <MapPin size={12} /> {item.warehouse}
                    </div>
                  </td>
                  <td style={tdStyle}><strong>{item.referenceNo || item.orderNumber || '---'}</strong></td>
                  <td style={tdStyle}>
                      <div style={{fontSize: '13px', fontWeight: '600'}}>{item.micNo}</div>
                  </td>
                  <td style={tdStyle}>{item.warehouse}</td>
                  <td style={tdStyle}>
                    <div style={{fontSize: '11px', color: '#16a34a', fontWeight:'bold', backgroundColor: '#dcfce7', padding: '4px 8px', borderRadius: '4px', display: 'inline-block'}}>
                        QC COMPLETED <CheckCircle size={12} style={{display: 'inline', verticalAlign: 'text-bottom'}}/>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      View <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

const filterBarStyle = { display: 'flex', gap: '15px', marginBottom: '20px' };
const searchInputStyle = { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

export default ReceivedList;
